import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Loader2, AlertCircle, Zap, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { errMsg } from "@/lib/format";
import { sound } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import OnboardingModal from "@/components/OnboardingModal";
import ManagePayoutsModal from "@/components/ManagePayoutsModal";

export default function Payouts() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [params] = useSearchParams();

  const load = async () => {
    try {
      const r = await api.get("/billing/connect/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Could not load payout status"));
      setStatus({ has_account: false });
    }
  };

  useEffect(() => {
    load();
    // Coming back from Stripe's hosted onboarding — Stripe can take a few
    // seconds to flip payouts_enabled, so poll briefly instead of making the
    // host refresh manually.
    if (params.get("onboarded")) {
      toast.success("Welcome back — checking your payout status…");
      let n = 0;
      const t = setInterval(async () => {
        n += 1;
        const r = await api.get("/billing/connect/status").catch(() => null);
        if (r?.data) setStatus(r.data);
        if (n >= 6 || r?.data?.payouts_enabled) clearInterval(t);
      }, 3000);
      return () => clearInterval(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onboard = () => {
    sound.play("tap");
    setOnboardOpen(true);
  };

  const openDashboard = () => {
    sound.play("tap");
    setManageOpen(true);
  };

  const ready = status?.payouts_enabled;
  const started = status?.has_account && !status?.payouts_enabled;
  const instantOk = !!status?.instant_eligible;

  const addDebitCard = () => {
    sound.play("tap");
    setManageOpen(true);
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <OnboardingModal
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        onComplete={(s) => {
          if (s) setStatus(s);
          load();
        }}
      />
      <ManagePayoutsModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onDone={(s) => {
          if (s) setStatus(s);
          load();
        }}
      />
      <div>
        <h1 className="text-3xl font-extrabold text-white">Payouts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Set up Stripe so you can collect everyone's shares and pay the merchant.
        </p>
      </div>

      <div className="hp-glass rounded-3xl p-8 text-center hp-fade-in">
        {status === null ? (
          <Loader2 className="animate-spin text-[#c084fc] mx-auto" />
        ) : ready ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl grid place-items-center bg-[#34d399]/20 text-[#34d399] mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="text-lg font-bold text-white">You're set up to get paid</h3>
            <p className="text-sm text-muted-foreground mt-1">
              When a bill is fully funded, you can release the money to yourself and pay
              the merchant.
            </p>

            {instantOk ? (
              <div
                data-testid="instant-enabled-pill"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-[#34d399] bg-[#34d399]/12 border border-[#34d399]/30"
              >
                <Zap size={15} /> Instant payouts on — your money lands in less than 30 minutes
              </div>
            ) : (
              <div
                data-testid="instant-warning"
                className="mt-5 text-left rounded-2xl p-4 border border-amber-400/30 bg-amber-400/[0.07]"
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl grid place-items-center bg-amber-400/15 text-amber-300 shrink-0">
                    <AlertCircle size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold text-amber-200">
                      Add a debit card — get paid in less than 30 minutes
                    </p>
                    <p className="text-[13px] text-amber-100/70 mt-1 leading-snug">
                      With a debit card, your money lands in{" "}
                      <span className="font-semibold text-amber-200">less than 30 minutes</span> —
                      any time, any day. It takes about a minute to add. (Without one, transfers
                      route to your bank instead and take a little longer.)
                    </p>
                    <Button
                      data-testid="add-debit-card-btn"
                      onClick={addDebitCard}
                      disabled={busy}
                      className="hp-cta mt-3 h-10 px-5 rounded-xl font-bold text-white gap-2 border-0"
                    >
                      <CreditCard size={16} /> Add a debit card
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="open-stripe-dashboard"
              onClick={openDashboard}
              disabled={busy}
              className="hp-cta mt-5 h-11 px-6 rounded-xl font-bold text-white gap-2 border-0"
            >
              <Wallet size={16} /> Manage payouts &amp; cards
            </Button>
          </>
        ) : (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl grid place-items-center bg-[#7c3aed]/20 text-[#c084fc] mb-4">
              {started ? <AlertCircle size={28} /> : <Wallet size={28} />}
            </div>
            <h3 className="text-lg font-bold text-white">
              {started ? "Finish your payout setup" : "Set up payouts"}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              {started
                ? "Stripe still needs a few details before payouts can be enabled."
                : "Connect a bank or debit card via Stripe to receive collected funds instantly."}
            </p>
            <Button
              data-testid="setup-payouts-btn"
              onClick={onboard}
              disabled={busy}
              className="hp-cta mt-5 h-11 px-6 rounded-xl font-bold text-white border-0"
            >
              {busy ? "Redirecting…" : started ? "Continue setup" : "Set up with Stripe"}
            </Button>
            {started && (
              <button
                data-testid="recheck-payouts-btn"
                onClick={load}
                className="block mx-auto mt-3 text-sm text-[#c084fc] font-semibold"
              >
                I finished on Stripe — check again
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
